import clsx from "clsx";
import { Link, navigate } from "gatsby";
import React, { forwardRef, useEffect, useState } from "react";

import { useAuthContext } from "../../../../auth";
import { useHeaderMenu } from "../../../../hooks/use-header-menu";
import { usePageFullPath } from "../../../../hooks/use-page-full-path";
import { getLocalizedPath } from "../../../../utils/pages";
import { BiesseLogo } from "../../../biesse-logo";
import { IconButton } from "../../../buttons/icon-button";
import { HeaderMenuItem } from "../../common/header-menu-item";
import { LocaleSelectorTag } from "../../common/locale-selector-tag";
import { HeaderMenuSpecialItems } from "../../types";

export interface HeaderMenuMainProps {
  className?: string;
}

export const HeaderMenuMain = forwardRef<HTMLDivElement, HeaderMenuMainProps>(
  ({ className }, ref) => {
    const {
      labels,
      toggleExpandedItemId,
      setItemPanelExpanded,
      expandedItemId,
      isItemPanelExpanded,
      headerMenu: { primaryItems, secondaryItems, node_locale, storeLocatorPage },
    } = useHeaderMenu();

    const { logged, userInfo } = useAuthContext();

    const iconButtonClassName = "!text-dark hover:!text-primary";

    const storeLocatorFullPath = usePageFullPath(storeLocatorPage?.id);

    // I'm using a state + effect to avoid rehydration errors due to missing window
    const [isStoreLocatorPage, setIsStoreLocatorPage] = useState(false);
    useEffect(() => {
      if (typeof window !== "undefined") {
        const isCurrentPage = !!window.location.pathname.match(
          new RegExp(`^${storeLocatorFullPath}`)
        );
        setIsStoreLocatorPage(!(isItemPanelExpanded && expandedItemId) && isCurrentPage);
      }
    }, [storeLocatorFullPath, isItemPanelExpanded, expandedItemId]);

    return (
      <div className={clsx("bg-neutral z-[110] block px-10 pt-4", className)} ref={ref}>
        <div className="xl:container !max-w-[1440px] mx-auto">
          <div className="flex justify-end gap-4 items-center">
            {secondaryItems?.map((item, i) =>
              item ? <HeaderMenuItem key={i} item={item} size="small" /> : null
            )}
            <LocaleSelectorTag
              locale={node_locale}
              onClick={() => toggleExpandedItemId(HeaderMenuSpecialItems.LocaleSelector)}
              variant="dark"
            />
          </div>
          <div className="flex items-center justify-between gap-8">
            <Link to={getLocalizedPath("/", node_locale)}>
              <BiesseLogo className="!text-primary -translate-y-2 !mb-1 h-14" />
            </Link>
            {primaryItems && primaryItems.length > 0 && (
              <div
                className={clsx(
                  "container mx-auto flex-1",
                  "flex justify-center gap-6 lg:gap-12 xl:gap-16 items-center"
                )}
              >
                {primaryItems.map((item, i) =>
                  item ? <HeaderMenuItem key={i} item={item} size="large" /> : null
                )}
              </div>
            )}
            <div className="flex items-center justify-end gap-3">
              {storeLocatorFullPath && (
                <IconButton
                  icon="map-pin"
                  title={labels["header-menu-store-locator-title"]}
                  className={clsx(iconButtonClassName, isStoreLocatorPage && "ring-2 ring-primary")}
                  onClick={() => {
                    setItemPanelExpanded(false);
                    navigate(storeLocatorFullPath);
                  }}
                  variant="light"
                  size="small"
                />
              )}
              <IconButton
                icon="search"
                title={labels["header-menu-search-title"]}
                className={clsx(
                  iconButtonClassName,
                  isItemPanelExpanded &&
                    expandedItemId === HeaderMenuSpecialItems.Search &&
                    "ring-2 ring-primary"
                )}
                onClick={() => toggleExpandedItemId(HeaderMenuSpecialItems.Search)}
                variant="light"
                size="small"
              />
              <IconButton
                icon="profile"
                title={
                  logged
                    ? [userInfo?.given_name, userInfo?.family_name].filter(Boolean).join(" ")
                    : labels["header-menu-personal-area-login-cta"]
                }
                className={clsx(
                  iconButtonClassName,
                  isItemPanelExpanded &&
                    expandedItemId === HeaderMenuSpecialItems.PersonalArea &&
                    "ring-2 ring-primary"
                )}
                onClick={() => toggleExpandedItemId(HeaderMenuSpecialItems.PersonalArea)}
                variant="light"
                size="small"
              />
            </div>
          </div>
        </div>
      </div>
    );
  }
);
